import React, {useEffect, useMemo, useState} from "react";
import {AppNavigator} from "../../components/AppNavigator";
import {userPool} from "../../userpool";
import "./AccountPage.css"
import {useNavigate} from "react-router-dom";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import {logout} from "../../services/auth/logout";
import {Accordion, AccordionDetails, AccordionSummary, Box, CardActionArea, CardContent, Paper, Skeleton, Stack, ToggleButton, ToggleButtonGroup} from "@mui/material";
import {ArrowForward, BookmarkBorderOutlined, DarkModeOutlined, ExpandMore, LightModeOutlined, SettingsBrightnessOutlined} from "@mui/icons-material";
import {ColorModePreference, useColorMode} from "../../theme/ColorModeContext";
import {PageContainer} from "../../components/layout/PageContainer";
import {generateClient, GraphQLResult} from "aws-amplify/api";
import {listUserSavedParks} from "../../graphql/queries";
import {ListUserSavedParksQuery, UserSavedParks} from "../../API";
import data from "../../data/searchData.json";
import {DEFAULT_GROUP} from "../../components/saved-parks/CreateBookMark";

export function AccountPage() {
  const user = userPool.getCurrentUser();
  const navigate = useNavigate();
  const client = generateClient()
  const {mode, preference, setPreference} = useColorMode()
  const parks = useMemo(() => {
    const options = new Map()
    data.forEach(park => {
      options.set(park.id, park)
    })
    return options
  }, [])
  const [savedParksIdsByGroup, setSavedParkIdsByGroup] = useState<Map<string, string[]>>(new Map())
  const [loadingSaved, setLoadingSaved] = useState<boolean>(true)


  useEffect(() => {
    if (user === null) {
      navigate("/")
    }
  }, [user, navigate])

  useEffect(() => {
    const userSavedPromise = client.graphql({
      query: listUserSavedParks
    }) as Promise<GraphQLResult<ListUserSavedParksQuery>>

    userSavedPromise.then((res) => {
      const parksIdsByGroup = new Map<string, string[]>()
      const savedParks = (res.data?.listUserSavedParks?.items ?? []).filter((item): item is UserSavedParks => Boolean(item))

      savedParks.forEach(savedPark => {
        const group: string = savedPark.group || DEFAULT_GROUP
        const parkIds = parksIdsByGroup.get(group) || []
        parksIdsByGroup.set(group, [savedPark.parkId, ...parkIds])
      })

      setSavedParkIdsByGroup(parksIdsByGroup)
    }).finally(() => setLoadingSaved(false))
  }, [])

  return <div className={'AccountPage'}>
    <div className={"AccountPageContent"}>
      <PageContainer sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
      <Typography component="h1" variant="h4">
        Welcome {user?.getUsername()}
      </Typography>
      <Paper variant="outlined" className={'AccountPage-appearance'}>
        <div className={'AccountPage-appearance-label'}>
          {mode === 'dark' ? <DarkModeOutlined/> : <LightModeOutlined/>}
          <Typography component="div" variant="subtitle1">Appearance</Typography>
        </div>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={preference}
          onChange={(_, value) => { if (value) setPreference(value as ColorModePreference) }}
          aria-label="Color mode"
        >
          <ToggleButton value="light" aria-label="Light mode">
            <LightModeOutlined fontSize="small" sx={{mr: 0.5}}/>Light
          </ToggleButton>
          <ToggleButton value="system" aria-label="Use system default">
            <SettingsBrightnessOutlined fontSize="small" sx={{mr: 0.5}}/>System
          </ToggleButton>
          <ToggleButton value="dark" aria-label="Dark mode">
            <DarkModeOutlined fontSize="small" sx={{mr: 0.5}}/>Dark
          </ToggleButton>
        </ToggleButtonGroup>
      </Paper>
      <Accordion defaultExpanded={true}>
        <AccordionSummary
          expandIcon={<ExpandMore/>}
          aria-controls="saved-parks-content"
          id="saved-parks-header"
        >
          <Typography component="div" variant="h5">
            Saved Parks
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={'AccountPage-saved-park'}>
          {loadingSaved ? (
            <Stack spacing={1.5}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} variant="rounded" height={92}/>
              ))}
            </Stack>
          ) : savedParksIdsByGroup.size === 0 ? (
            <Box sx={{textAlign: 'center', py: 4, px: 2, color: 'text.secondary'}}>
              <BookmarkBorderOutlined sx={{fontSize: 40, mb: 1, opacity: 0.6}}/>
              <Typography component="div" variant="subtitle1" color="text.primary">No saved parks yet</Typography>
              <Typography component="div" variant="body2" sx={{mb: 2, mt: 0.5}}>
                Bookmark a park to find it here later.
              </Typography>
              <Button variant="outlined" onClick={() => navigate('/search')}>Explore parks</Button>
            </Box>
          ) : Array.from(savedParksIdsByGroup.keys()).map(key => {
            return <Accordion defaultExpanded={true} key={key}>
              <AccordionSummary
                expandIcon={<ExpandMore/>}
                aria-controls={`saved-group-${key}-content`}
                id={`saved-group-${key}-header`}
              >
                <Typography component="div" variant="h6">{key}
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={'AccountPage-saved-park'}>
                {savedParksIdsByGroup.get(key)?.map(id => {
                  const park = parks.get(id)
                  return <Paper key={id} variant="outlined">
                    <CardActionArea onClick={() => navigate(`/search/${id}`)}>
                      <CardContent className={"AccountPage-saved-park-information"}>
                        <div className={"AccountPage-saved-park-information-header"}>
                          <Typography variant="h6" component="div">{park.name}</Typography>
                          <ArrowForward/>
                        </div>
                        <Typography variant="body1" component="div">{park.description}</Typography>
                      </CardContent>
                    </CardActionArea>
                  </Paper>
                })}
              </AccordionDetails>
            </Accordion>
          })}

        </AccordionDetails>
      </Accordion>
      <Button
        onClick={logout}
        type="submit"
        fullWidth
        variant="contained"
        sx={{mt: 3, mb: 2}}
      >
        Log out
      </Button>
      </PageContainer>
    </div>
    <AppNavigator/>
  </div>
}